import { useCallback, useState } from 'react';
import { ImageFormat } from 'corgi-types';
import type { GQLLocale } from 'graphql-ts-types';

import cookieStorage from 'common/CookieStorage';
import { EventBusEvent } from 'common/enums/EventBus';
import eventBus from 'common/event-bus/EventBus';
import { executeQuery } from 'common/gql';
import articleLabelsFragment from 'common/gql/fragments/articleLabels.gql';
import cartAttributes from 'common/gql/fragments/cart.gql';
import shippingInfoAttributes from 'common/gql/fragments/shippingInfo.gql';
import shopAttributes from 'common/gql/fragments/shop.gql';
import cartResponseFragment from 'common/gql/mutation/cart/cart-response-fragment.gql';
import type { Cart, CartResponse } from 'common/gql/mutation/cart/types';
import { combineQueryWithFragments } from 'common/gql/utils/fragments';
import { useGQLLocale } from 'common/hooks/locale';
import isWebpSupported from 'common/utils/isWebpSupported';
import removeFromCartQuery from '../../../cart-product-list/gql/mutation/remove-from-cart.gql';
import updateCartItemV2Query from '../../../cart-product-list/gql/mutation/update-cart-item-v2.gql';

interface CartItemState {
  quantity: number;
  isLoading: boolean;
}

type CartItem = { quantity: number; article: { sku: string } };

interface UseAccessoryCartReturn {
  cartItems: Map<string, CartItemState>;
  handleAddToCart: (sku: string, cart: Cart) => void;
  handleQuantityChange: (sku: string, quantity: number) => Promise<void>;
  handleRemove: (sku: string) => Promise<void>;
}

const emitCounterUpdate = (items: CartItem[]) => {
  const count = items.reduce((counter, { quantity }) => counter + quantity, 0);
  eventBus.emit(EventBusEvent.CartCounterUpdate, count);
};

const updateQuantityQuery = combineQueryWithFragments(
  updateCartItemV2Query,
  cartResponseFragment,
  cartAttributes,
  shopAttributes,
  shippingInfoAttributes,
  articleLabelsFragment
);

const removeItemQuery = combineQueryWithFragments(
  removeFromCartQuery,
  cartResponseFragment,
  cartAttributes,
  shopAttributes,
  shippingInfoAttributes,
  articleLabelsFragment
);

const executeUpdateQuantity = async (sku: string, quantity: number, locale: GQLLocale) => {
  const zipCode = cookieStorage.getZipCode();

  return executeQuery<CartResponse>({
    query: updateQuantityQuery,
    variables: {
      locale,
      zipCode,
      item: { sku, quantity, services: [] },
      ...(isWebpSupported() && { imageFormat: ImageFormat.WEBP }),
    },
    useApq: true,
  });
};

const executeRemoveItem = async (sku: string, locale: GQLLocale) => {
  const zipCode = cookieStorage.getZipCode();

  return executeQuery<CartResponse>({
    query: removeItemQuery,
    variables: {
      sku,
      locale,
      zipCode,
      ...(isWebpSupported() && { imageFormat: ImageFormat.WEBP }),
    },
    useApq: true,
  });
};

export const useAccessoryCart = (): UseAccessoryCartReturn => {
  const [cartItems, setCartItems] = useState<Map<string, CartItemState>>(new Map());
  const locale = useGQLLocale();

  const handleAddToCart = useCallback((sku: string, cart: Cart) => {
    const items = cart.items as CartItem[];
    const cartItem = items.find(item => item.article.sku === sku);
    const quantity = cartItem?.quantity ?? 1;
    setCartItems(prev => new Map(prev).set(sku, { quantity, isLoading: false }));
  }, []);

  const handleQuantityChange = useCallback(
    async (sku: string, newQuantity: number) => {
      const originalQuantity = cartItems.get(sku)?.quantity ?? newQuantity;
      setCartItems(prev => new Map(prev).set(sku, { quantity: newQuantity, isLoading: true }));

      try {
        const res = await executeUpdateQuantity(sku, newQuantity, locale);
        if (res.data?.cart) {
          emitCounterUpdate(res.data.cart.items as CartItem[]);
        }
        setCartItems(prev => new Map(prev).set(sku, { quantity: newQuantity, isLoading: false }));
      } catch {
        setCartItems(prev =>
          new Map(prev).set(sku, { quantity: originalQuantity, isLoading: false })
        );
      }
    },
    [cartItems, locale]
  );

  const handleRemove = useCallback(
    async (sku: string) => {
      setCartItems(prev => {
        const current = prev.get(sku);

        return current ? new Map(prev).set(sku, { ...current, isLoading: true }) : prev;
      });

      try {
        const res = await executeRemoveItem(sku, locale);
        if (res.data?.cart) {
          emitCounterUpdate(res.data.cart.items as CartItem[]);
        }
        setCartItems(prev => {
          const next = new Map(prev);
          next.delete(sku);

          return next;
        });
      } catch {
        setCartItems(prev => {
          const current = prev.get(sku);

          return current ? new Map(prev).set(sku, { ...current, isLoading: false }) : prev;
        });
      }
    },
    [locale]
  );

  return { cartItems, handleAddToCart, handleQuantityChange, handleRemove };
};
